import React from 'react';
import { FiDollarSign, FiAlertCircle } from 'react-icons/fi';
import {
  FormSection,
  SectionTitle,
  SubSectionTitle,
  FormGrid,
  FormGroup,
  Label,
  Input,
  TextArea,
  RadioGroup,
  RadioLabel,
  InfoBox,
  HelpText
} from './FormStyles';
import { Candidate } from '@/models';
import { DocumentUpload, UploadedDocument } from './DocumentUpload';
import { DOCUMENTS_BY_SECTION } from '@/config/documentsBySection';

interface FinancialComplementarySectionProps {
  data: Partial<Candidate>;
  onChange: (data: Partial<Candidate>) => void;
  readonly?: boolean;
  uploadedDocuments?: UploadedDocument[];
  onDocumentUpload?: (sectionKey: string, docType: string, file: File) => Promise<void>;
  onDocumentRemove?: (sectionKey: string, docType: string) => void;
}

export const FinancialComplementarySection: React.FC<FinancialComplementarySectionProps> = ({
  data,
  onChange,
  readonly = false,
  uploadedDocuments = [],
  onDocumentUpload,
  onDocumentRemove
}) => {
  const handleChange = (field: string, value: any) => {
    onChange({ ...data, [field]: value });
  };

  // Obtenir le profil type pour charger les bons documents
  const profileType = data.profile_type || 'employee';
  const documentsConfig = DOCUMENTS_BY_SECTION[profileType as keyof typeof DOCUMENTS_BY_SECTION];
  const financialDocs = documentsConfig?.financial_complementary || [];
  const specialSituationDocs = documentsConfig?.special_situations || [];

  // Fonctions wrapper pour adapter les props au format attendu par DocumentUpload
  const handleFileSelect = (sectionKey: string) => async (documentType: string, file: File | null) => {
    if (file && onDocumentUpload) {
      await onDocumentUpload(sectionKey, documentType, file);
    }
  };

  const handleFileRemove = (sectionKey: string) => (documentType: string) => {
    if (onDocumentRemove) {
      onDocumentRemove(sectionKey, documentType);
    }
  };

  // Filtrer les documents uploadés par types de documents de la section
  const getUploadedFilesForSection = (documentsConfig: any[]) => {
    const documentTypes = documentsConfig.map(doc => doc.type);
    return uploadedDocuments.filter(doc => documentTypes.includes(doc.type));
  };

  return (
    <>
      <FormSection>
        <SectionTitle>
          <FiDollarSign />
          Documents Financiers Complémentaires
        </SectionTitle>

        <InfoBox $type="info">
          <p>
            Ces informations complémentaires permettent d'avoir une vision complète de votre situation financière
            et peuvent renforcer votre dossier.
          </p>
        </InfoBox>




        <SubSectionTitle>Documents Justificatifs</SubSectionTitle>

        {onDocumentUpload && onDocumentRemove && financialDocs.length > 0 && (
          <DocumentUpload
            sectionTitle="Documents Financiers Complémentaires"
            documents={financialDocs}
            uploadedFiles={getUploadedFilesForSection(financialDocs)}
            onFileSelect={handleFileSelect('financial_complementary')}
            onFileRemove={handleFileRemove('financial_complementary')}
            readonly={readonly}
          />
        )}
      </FormSection>

      <FormSection>
        <SectionTitle>
          <FiAlertCircle />
          Situations Particulières
        </SectionTitle>

        <InfoBox $type="info">
          <p>
            Si vous êtes concerné(e) par l'une de ces situations, merci de l'indiquer.
            Cela peut vous donner accès à des dispositions particulières.
          </p>
        </InfoBox>

        <SubSectionTitle>Situation de Handicap ou Invalidité</SubSectionTitle>

        <FormGroup>
          <Label>Possédez-vous un certificat médical de handicap ?</Label>
          <RadioGroup>
            <RadioLabel>
              <input
                type="radio"
                value="true"
                checked={data.a_certificat_medical_handicap === true}
                onChange={() => handleChange('a_certificat_medical_handicap', true)}
                disabled={readonly}
              />
              Oui
            </RadioLabel>
            <RadioLabel>
              <input
                type="radio"
                value="false"
                checked={data.a_certificat_medical_handicap === false}
                onChange={() => handleChange('a_certificat_medical_handicap', false)}
                disabled={readonly}
              />
              Non
            </RadioLabel>
          </RadioGroup>
        </FormGroup>

        {data.a_certificat_medical_handicap && (
          <FormGroup>
            <Label>Détails du handicap</Label>
            <TextArea
              value={data.details_handicap || ''}
              onChange={e => handleChange('details_handicap', e.target.value)}
              placeholder="Précisez la nature du handicap et les aménagements nécessaires..."
              rows={3}
              disabled={readonly}
            />
          </FormGroup>
        )}

        <FormGroup>
          <Label>Possédez-vous une carte d'invalidité ?</Label>
          <RadioGroup>
            <RadioLabel>
              <input
                type="radio"
                value="true"
                checked={data.a_carte_invalidite === true}
                onChange={() => handleChange('a_carte_invalidite', true)}
                disabled={readonly}
              />
              Oui
            </RadioLabel>
            <RadioLabel>
              <input
                type="radio"
                value="false"
                checked={data.a_carte_invalidite === false}
                onChange={() => handleChange('a_carte_invalidite', false)}
                disabled={readonly}
              />
              Non
            </RadioLabel>
          </RadioGroup>
        </FormGroup>

        {data.a_carte_invalidite && (
          <FormGroup>
            <Label>Taux d'invalidité (%)</Label>
            <Input
              type="number"
              min="0"
              max="100"
              value={data.taux_invalidite || ''}
              onChange={e => handleChange('taux_invalidite', parseInt(e.target.value) || 0)}
              placeholder="Ex: 50"
              disabled={readonly}
            />
            <HelpText>Taux d'invalidité indiqué sur votre carte</HelpText>
          </FormGroup>
        )}

        <SubSectionTitle>Protection Juridique</SubSectionTitle>

        <FormGroup>
          <Label>Possédez-vous une ordonnance de protection (violences) ?</Label>
          <RadioGroup>
            <RadioLabel>
              <input
                type="radio"
                value="true"
                checked={data.a_ordonnance_protection === true}
                onChange={() => handleChange('a_ordonnance_protection', true)}
                disabled={readonly}
              />
              Oui
            </RadioLabel>
            <RadioLabel>
              <input
                type="radio"
                value="false"
                checked={data.a_ordonnance_protection === false}
                onChange={() => handleChange('a_ordonnance_protection', false)}
                disabled={readonly}
              />
              Non
            </RadioLabel>
          </RadioGroup>
          <HelpText>
            Cette information reste confidentielle et peut vous donner un accès prioritaire au logement
          </HelpText>
        </FormGroup>

        {data.a_ordonnance_protection && (
          <FormGroup>
            <Label>Détails (facultatif)</Label>
            <TextArea
              value={data.details_protection || ''}
              onChange={e => handleChange('details_protection', e.target.value)}
              placeholder="Si vous souhaitez apporter des précisions..."
              rows={3}
              disabled={readonly}
            />
            <HelpText>Ces informations restent strictement confidentielles</HelpText>
          </FormGroup>
        )}

        <SubSectionTitle>Documents Justificatifs</SubSectionTitle>

        {onDocumentUpload && onDocumentRemove && specialSituationDocs.length > 0 && (
          <DocumentUpload
            sectionTitle="Situations Particulières"
            documents={specialSituationDocs}
            uploadedFiles={getUploadedFilesForSection(specialSituationDocs)}
            onFileSelect={handleFileSelect('special_situations')}
            onFileRemove={handleFileRemove('special_situations')}
            readonly={readonly}
          />
        )}
      </FormSection>
    </>
  );
};
